import { Box, Button } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { labels } from '../../../Constants';
import {
  useGetClientStatisticsGroupedQuery,
  useGetJobStatisticsGroupedQuery,
  useGetTalentStatisticsGroupedQuery,
  useGetTalentStatisticsQuery,
} from '../../../Redux/api/statisticsApi';
import { KaziLayout } from '../../../Shared Components/Kazi/Layouts/KaziLayout';
import { KaziJobsBarChart } from '../../../Shared Components/statistics/KaziJobsBarChart';
import { KaziPieChart } from '../../../Shared Components/statistics/KaziPieChart';
import { KaziTotalVsHiredBarChart } from '../../../Shared Components/statistics/KaziTotalVsHiredBarChart';
import { exportToCSV } from '../../../Utils/Charts';

interface ChartWrapperInjectedProps {
  chartKey: string;
  title: string;
  countTitle?: string;
  xAxisLabel?: string;
  dataset: any[];
}

interface ChartWrapperProps {
  showExportButton?: boolean;
  dataset: any[];
  chartKey: string;
  title: string;
  countTitle?: string; // Optional if not different from title
  xAxisLabel?: string;
  children: (props: ChartWrapperInjectedProps) => React.ReactNode; // Function as children
}

const ChartWrapper = ({
  showExportButton = false,
  dataset,
  chartKey,
  title,
  countTitle,
  xAxisLabel,
  children,
}: ChartWrapperProps) => {
  const fileName = title.replace(/\s+/g, '_'); // Use title as export file name with underscores

  return (
    <Box
      sx={{
        position: 'relative', // Required to make the Box the reference point for absolute positioning
      }}
    >
      {/* Conditionally Render the Export Button */}
      {showExportButton && (
        <Box
          sx={{
            position: 'absolute', // Positions the button inside the card
            top: 24, // Adjust the value to match desired space from the top
            right: 42, // Adjust the value to align the button with the right edge
            zIndex: 1, // Ensures it appears above the chart/content
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => exportToCSV(dataset, fileName)}
          >
            Export
          </Button>
        </Box>
      )}

      {children({
        chartKey,
        title,
        countTitle,
        xAxisLabel,
        dataset,
      })}
    </Box>
  );
};

export const Statistics = () => {
  const navigate = useNavigate();

  const navigateToAdminManageUsers = (params?: { [param: string]: string }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach(key => {
        searchParams.set(key, params[key]);
      });
    }
    navigate({
      pathname: '/admin/manage/users',
      search: searchParams.toString(),
    });
  };

  const {
    data: talentsData,
    // isLoading,
    // isFetching,
  } = useGetTalentStatisticsQuery();

  const {
    data: groupedTalentsData,
    // isLoading,
    // isFetching,
  } = useGetTalentStatisticsGroupedQuery();

  const {
    data: jobsData,
    // isLoading,
    // isFetching,
  } = useGetJobStatisticsGroupedQuery();

  const {
    data: groupedClientsData,
    // isLoading,
    // isFetching,
  } = useGetClientStatisticsGroupedQuery();

  return (
    <KaziLayout>
      <ChartWrapper
        chartKey="talentsData"
        title="Talents Overview"
        countTitle="Talents"
        dataset={[
          { label: labels.total, value: talentsData?.totalTalents ?? 0 },
          {
            label: labels.completeProfiles,
            value: talentsData?.completedProfileTalents ?? 0,
          },
          { label: labels.hired, value: talentsData?.hiredTalents ?? 0 },
        ]}
      >
        {({ chartKey, title, countTitle, dataset }) => (
          <KaziPieChart
            chartKey={chartKey}
            title={title}
            countTitle={countTitle}
            dataset={dataset}
            showPercentages={false}
            onItemClick={item => {
              const params: Record<string, string> = { type: labels.talent };

              if (item?.label === labels.completeProfiles) {
                params.profile = labels.completed || item.label || '';
              } else if (item?.label === labels.hired) {
                params.hired = item.label || '';
              }

              navigateToAdminManageUsers(params);
            }}
          />
        )}
      </ChartWrapper>

      <ChartWrapper
        chartKey="byCountry"
        title="Talents By Country"
        dataset={groupedTalentsData?.byCountry ?? []}
        xAxisLabel="Country"
      >
        {({ chartKey, title, dataset, xAxisLabel }) => (
          <KaziTotalVsHiredBarChart
            chartKey={chartKey}
            title={title}
            dataset={dataset}
            xAxisLabel={xAxisLabel}
            onItemClick={item => {
              const params: Record<string, string> = {
                type: labels.talent,
                country: item.label,
              };

              navigateToAdminManageUsers(params);
            }}
          />
        )}
      </ChartWrapper>

      <ChartWrapper
        chartKey="bySpecialization"
        title="Talents By Specialization"
        dataset={groupedTalentsData?.bySpecialization ?? []}
        xAxisLabel="Specialization"
      >
        {({ chartKey, title, dataset, xAxisLabel }) => (
          <KaziTotalVsHiredBarChart
            chartKey={chartKey}
            title={title}
            dataset={dataset}
            xAxisLabel={xAxisLabel}
            onItemClick={item => {
              const params: Record<string, string> = {
                type: labels.talent,
                specialization: item.label,
              };

              navigateToAdminManageUsers(params);
            }}
          />
        )}
      </ChartWrapper>

      <ChartWrapper
        chartKey="byAffiliate"
        title="Talents By Affiliate"
        countTitle="Talents"
        dataset={
          groupedTalentsData?.byAffiliate.map(({ label, totalCount }) => ({
            label,
            value: totalCount,
          })) ?? []
        }
      >
        {({ chartKey, title, countTitle, dataset }) => (
          <KaziPieChart
            chartKey={chartKey}
            title={title}
            countTitle={countTitle}
            dataset={dataset}
            onItemClick={item => {
              const params: Record<string, string> = { type: labels.talent };

              if (item?.label === labels.unspecified) {
                params.profile = labels.all || item.label || '';
              } else {
                params.affiliate = item.label || '';
              }

              navigateToAdminManageUsers(params);
            }}
          />
        )}
      </ChartWrapper>

      <ChartWrapper
        chartKey="jobs"
        title="Jobs"
        dataset={[jobsData]}
        xAxisLabel="Status"
      >
        {({ chartKey, title, dataset, xAxisLabel }) => (
          <KaziJobsBarChart
            chartKey={chartKey}
            title={title}
            dataset={dataset[0]}
            xAxisLabel={xAxisLabel}
          />
        )}
      </ChartWrapper>

      <ChartWrapper
        chartKey="clientsByCountry"
        title="Clients By Country"
        countTitle="Clients"
        dataset={
          groupedClientsData?.byCountry.map(({ label, count }) => ({
            label,
            value: count,
          })) ?? []
        }
      >
        {({ chartKey, title, countTitle, dataset }) => (
          <KaziPieChart
            chartKey={chartKey}
            title={title}
            countTitle={countTitle}
            dataset={dataset}
            onItemClick={item => {
              const params: Record<string, string> = {
                type: labels.client,
                country: item.label,
              };

              navigateToAdminManageUsers(params);
            }}
          />
        )}
      </ChartWrapper>

      <ChartWrapper
        chartKey="clientsByType"
        title="Clients By Type"
        countTitle="Clients"
        dataset={
          groupedClientsData?.byType.map(({ label, count }) => ({
            label,
            value: count,
          })) ?? []
        }
      >
        {({ chartKey, title, countTitle, dataset }) => (
          <KaziPieChart
            chartKey={chartKey}
            title={title}
            countTitle={countTitle}
            dataset={dataset}
          />
        )}
      </ChartWrapper>
    </KaziLayout>
  );
};
